// ABOUTME: Turns the card a ref points at into a PNG: download it under a prefixed name, or copy it to the clipboard.
// ABOUTME: Notifies the user of the outcome; the caller only wires the ref and the two actions.

import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { copyImageToClipboard } from "@shared/utils/copyImageToClipboard"
import { captureElement } from "@shared/utils/captureElement"

export function useShareImage(filePrefix: string) {
  const { t } = useTranslation()
  const { handleNotification } = useNotificationContext()
  const cardRef = useRef<HTMLDivElement>(null)
  const [generating, setGenerating] = useState(false)

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return null

    setGenerating(true)
    try {
      return await captureElement(cardRef.current, null)
    } finally {
      setGenerating(false)
    }
  }, [])

  const handleDownload = async () => {
    try {
      const canvas = await generateImage()
      if (!canvas) return

      const link = document.createElement("a")
      link.download = `${filePrefix}-${Date.now()}.png`
      link.href = canvas.toDataURL("image/png")
      link.click()
      handleNotification("success", t("statistics.share.saved"), false, 2000)
    } catch {
      handleNotification("error", t("statistics.share.copyError"), false, 3000)
    }
  }

  const handleCopy = async () => {
    try {
      const mode = await copyImageToClipboard(
        async () => {
          const canvas = await generateImage()
          if (!canvas) throw new Error("canvas generation failed")
          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob((b) => resolve(b), "image/png")
          })
          if (!blob) throw new Error("blob generation failed")
          return blob
        },
        async () => {
          const canvas = await generateImage()
          if (!canvas) throw new Error("canvas generation failed")
          return canvas.toDataURL("image/png")
        },
      )
      const messageKey = mode === "native" ? "statistics.share.copied" : "statistics.share.copiedAsUrl"
      handleNotification("success", t(messageKey), false, 2000)
    } catch {
      handleNotification("error", t("statistics.share.copyError"), false, 3000)
    }
  }

  return { cardRef, generating, handleDownload, handleCopy }
}
